import { useState, useEffect } from 'react';
import axios from 'axios';
import { Check, X, Clock, Loader2, Sparkles } from 'lucide-react';
import { ALL_TIME_SLOTS, endTimesFor, formatTime12 } from '../utils/timeSlots';
import DatePicker from './DatePicker';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Three-button block shown on cleaner bookings sitting in
 * `awaiting_cleaner_response` — Accept, Decline, Suggest change.
 * onResolved(newStatus) fires after the API call so the parent can refetch.
 */
const CleanerBookingResponseActions = ({ booking, onResolved }) => {
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState(null); // null | "decline" | "propose" | "flex-accept"
  const [declineReason, setDeclineReason] = useState('');
  const [newDate, setNewDate] = useState(booking.service_date || '');
  const [newStart, setNewStart] = useState(booking.start_time || booking.service_time || '10:00');
  const [newEnd, setNewEnd] = useState(booking.end_time || '12:00');
  const [note, setNote] = useState('');
  const [error, setError] = useState(null);
  // For flexible bookings: cleaner picks a start time within the customer's window
  const [chosenStart, setChosenStart] = useState(booking.flexible_window_start || '10:00');
  const [optimal, setOptimal] = useState(null);  // { optimal_start_time, valid_start_times, optimal_total_travel_minutes }

  // When the user opens the flex-accept form, fetch the cleaner's optimal slot from backend
  useEffect(() => {
    if (mode !== 'flex-accept' || !booking.flexible) return;
    const fetchOptimal = async () => {
      try {
        const token = localStorage.getItem('token');
        const r = await axios.get(`${API}/bookings/${booking.id}/optimal-start-time`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setOptimal(r.data);
        if (r.data.optimal_start_time) {
          setChosenStart(r.data.optimal_start_time);
        }
      } catch {
        // Silent — fall back to the simple in-window dropdown
      }
    };
    fetchOptimal();
  }, [mode]);  // eslint-disable-line react-hooks/exhaustive-deps

  const post = async (payload) => {
    setBusy(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const r = await axios.post(
        `${API}/bookings/${booking.id}/cleaner-response`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      onResolved && onResolved(r.data.status);
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not submit response');
    } finally {
      setBusy(false);
    }
  };

  if (mode === 'flex-accept') {
    // Prefer backend-validated candidates (account for overlaps + travel time);
    // fall back to all in-window slots if the API call didn't return anything.
    const backendCandidates = optimal?.valid_start_times || [];
    const useBackend = backendCandidates.length > 0;
    const validStarts = useBackend
      ? backendCandidates.map((c) => c.start_time)
      : ALL_TIME_SLOTS.filter((slot) => {
          const [sh, sm] = slot.split(':').map(Number);
          const startMin = sh * 60 + sm;
          const [wsh, wsm] = (booking.flexible_window_start || '00:00').split(':').map(Number);
          const [weh, wem] = (booking.flexible_window_end || '23:59').split(':').map(Number);
          const wStart = wsh * 60 + wsm;
          const wEnd = weh * 60 + wem;
          const duration = Math.round((booking.duration_hours || 2) * 60);
          return startMin >= wStart && startMin + duration <= wEnd;
        });
    const optimalTime = optimal?.optimal_start_time;
    const travelInfo = useBackend
      ? backendCandidates.find((c) => c.start_time === chosenStart)
      : null;
    return (
      <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-xl space-y-2" data-testid="flex-accept-form">
        <p className="text-xs text-green-800 font-medium">
          Pick a start time within {booking.flexible_window_start}–{booking.flexible_window_end}
          ({booking.duration_hours} hr job)
        </p>
        {optimalTime && (
          <p className="text-[11px] text-amber-700 inline-flex items-center gap-1" data-testid="optimal-hint">
            <Sparkles className="w-3 h-3" />
            Optimal slot: <strong>{formatTime12(optimalTime)}</strong>
            {optimal.optimal_total_travel_minutes != null && optimal.optimal_total_travel_minutes > 0 && (
              <span className="ml-1">({optimal.optimal_total_travel_minutes} min total drive that day)</span>
            )}
          </p>
        )}
        <select
          value={chosenStart}
          onChange={(e) => setChosenStart(e.target.value)}
          className="input-field text-sm w-full"
          data-testid="flex-chosen-start"
        >
          {validStarts.map((s) => (
            <option key={s} value={s}>
              {`${formatTime12(s)}${s === optimalTime ? '  ✨  optimal' : ''}`}
            </option>
          ))}
        </select>
        {travelInfo && (travelInfo.travel_in_minutes > 0 || travelInfo.travel_out_minutes > 0) && (
          <p className="text-[10px] text-text-secondary" data-testid="flex-travel-summary">
            🚗 {travelInfo.travel_in_minutes} min in · {travelInfo.travel_out_minutes} min out
          </p>
        )}
        {error && <div className="text-xs text-red-700">{error}</div>}
        <div className="flex gap-2">
          <button
            onClick={() => post({ action: 'accept', chosen_start_time: chosenStart })}
            disabled={busy || !chosenStart || validStarts.length === 0}
            className="flex-1 bg-green-600 hover:bg-green-700 text-white text-xs font-semibold px-3 py-1.5 rounded-full disabled:opacity-50 inline-flex items-center justify-center gap-1"
            data-testid="confirm-flex-accept-button"
          >
            {busy && <Loader2 className="w-3 h-3 animate-spin" />}
            Accept at {chosenStart && formatTime12(chosenStart)}
          </button>
          <button
            onClick={() => { setMode(null); setError(null); }}
            disabled={busy}
            className="flex-1 bg-surface border border-border text-text-secondary text-xs font-semibold px-3 py-1.5 rounded-full hover:border-primary"
          >
            Back
          </button>
        </div>
      </div>
    );
  }

  if (mode === 'decline') {
    return (
      <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-xl space-y-2" data-testid="decline-form">
        <p className="text-xs text-red-700 font-medium">
          Decline this booking? The customer will be refunded.
        </p>
        <textarea
          value={declineReason}
          onChange={(e) => setDeclineReason(e.target.value)}
          placeholder="Reason (optional, shared with customer)"
          maxLength={500}
          rows={2}
          className="input-field text-sm"
          data-testid="decline-reason-input"
        />
        {error && <div className="text-xs text-red-700">{error}</div>}
        <div className="flex gap-2">
          <button
            onClick={() => post({ action: 'decline', decline_reason: declineReason })}
            disabled={busy}
            className="flex-1 bg-red-500 hover:bg-red-600 text-white text-xs font-semibold px-3 py-1.5 rounded-full disabled:opacity-50 inline-flex items-center justify-center gap-1"
            data-testid="confirm-decline-button"
          >
            {busy && <Loader2 className="w-3 h-3 animate-spin" />}
            Confirm decline
          </button>
          <button
            onClick={() => { setMode(null); setError(null); }}
            disabled={busy}
            className="flex-1 bg-surface border border-border text-text-secondary text-xs font-semibold px-3 py-1.5 rounded-full hover:border-primary"
          >
            Back
          </button>
        </div>
      </div>
    );
  }

  if (mode === 'propose') {
    return (
      <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-xl space-y-2" data-testid="propose-form">
        <p className="text-xs text-amber-800 font-medium">Suggest a different time</p>
        <div className="grid grid-cols-3 gap-2">
          <DatePicker
            value={newDate}
            onChange={setNewDate}
            placeholder="Date"
            dataTestId="propose-date-input"
            className="text-sm"
          />
          <select
            value={newStart}
            onChange={(e) => {
              setNewStart(e.target.value);
              // Auto-bump end if it's now invalid
              const ends = endTimesFor(e.target.value);
              if (!ends.includes(newEnd)) setNewEnd(ends[0] || newEnd);
            }}
            className="input-field text-sm"
            data-testid="propose-start-select"
          >
            {ALL_TIME_SLOTS.map((s) => (
              <option key={s} value={s}>{formatTime12(s)}</option>
            ))}
          </select>
          <select
            value={newEnd}
            onChange={(e) => setNewEnd(e.target.value)}
            className="input-field text-sm"
            data-testid="propose-end-select"
          >
            {endTimesFor(newStart).map((s) => (
              <option key={s} value={s}>{formatTime12(s)}</option>
            ))}
          </select>
        </div>
        <textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Note to customer (optional)"
          maxLength={500}
          rows={2}
          className="input-field text-sm"
          data-testid="propose-note-input"
        />
        {error && <div className="text-xs text-red-700">{error}</div>}
        <div className="flex gap-2">
          <button
            onClick={() => post({
              action: 'propose_change',
              new_service_date: newDate,
              new_start_time: newStart,
              new_end_time: newEnd,
              note,
            })}
            disabled={busy || !newDate || !newStart || !newEnd}
            className="flex-1 bg-amber-500 hover:bg-amber-600 text-white text-xs font-semibold px-3 py-1.5 rounded-full disabled:opacity-50 inline-flex items-center justify-center gap-1"
            data-testid="confirm-propose-button"
          >
            {busy && <Loader2 className="w-3 h-3 animate-spin" />}
            Send proposal
          </button>
          <button
            onClick={() => { setMode(null); setError(null); }}
            disabled={busy}
            className="flex-1 bg-surface border border-border text-text-secondary text-xs font-semibold px-3 py-1.5 rounded-full hover:border-primary"
          >
            Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-3 flex gap-2 flex-wrap" data-testid="cleaner-response-actions">
      <button
        onClick={() => booking.flexible ? setMode('flex-accept') : post({ action: 'accept' })}
        disabled={busy}
        className="flex-1 min-w-[100px] bg-green-600 hover:bg-green-700 text-white text-xs font-semibold px-3 py-2 rounded-full disabled:opacity-50 inline-flex items-center justify-center gap-1"
        data-testid="accept-booking-button"
      >
        {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
        {booking.flexible ? 'Accept (pick time)' : 'Accept'}
      </button>
      <button
        onClick={() => setMode('propose')}
        disabled={busy}
        className="flex-1 min-w-[100px] bg-amber-500 hover:bg-amber-600 text-white text-xs font-semibold px-3 py-2 rounded-full disabled:opacity-50 inline-flex items-center justify-center gap-1"
        data-testid="suggest-change-button"
      >
        <Clock className="w-3 h-3" />
        Suggest change
      </button>
      <button
        onClick={() => setMode('decline')}
        disabled={busy}
        className="flex-1 min-w-[100px] bg-surface border border-red-300 text-red-600 hover:bg-red-50 text-xs font-semibold px-3 py-2 rounded-full disabled:opacity-50 inline-flex items-center justify-center gap-1"
        data-testid="decline-booking-button"
      >
        <X className="w-3 h-3" />
        Decline
      </button>
      {error && <p className="w-full text-xs text-red-700">{error}</p>}
    </div>
  );
};

export default CleanerBookingResponseActions;
